import * as React from "react";
// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CirclePlus, Save, Trash2 } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { FileUpload } from "@/components/FileUpload";
import { useToast } from "@/components/ui/use-toast";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Services
import { postDebt } from "@/services/finance";
import type { CreateDebtRequest } from "@/types/finance";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/finance-debt/add/"
)({
  component: AddFinanceDebt,
});

function AddFinanceDebt() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = localStorage.getItem("companyId");
  const [year, setYear] = React.useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    if (!storedYear) return undefined;
    const parsed = Number(storedYear);
    return isNaN(parsed) ? undefined : parsed;
  });

  const [yearError, setYearError] = React.useState<string>("");
  const [docFile, setDocFile] = React.useState("");
  const yearInputRef = React.useRef<HTMLDivElement>(null);

  const [revolvingDebts, setRevolvingDebts] = React.useState<
    Array<{
      name: string;
      amount: string;
      remark: string;
    }>
  >([
    { name: "ເງິນ​ເບີກ​ເກີນ​ບັນ​ຊີ", amount: "", remark: "" },
    { name: "ໜີ້​ຕ້ອງ​ສົ່ງ​ການ​ຄ້າ ແລະ ​ອື່ນໆ", amount: "", remark: "" },
    {
      name: "ໜີ້​ສິນ​ການ​ເງິນ,ເງິນ​ກູ້​ຢືມໄລ​ຍະ​ສັ້ນ",
      amount: "",
      remark: "",
    },
    { name: "ໜີ້​ຕ້ອງ​ສົ່ງ​ລັດ(ຄ່າພາ​ສີ-ອາ​ກອນ)", amount: "", remark: "" },
    {
      name: "ພັນ​ທະໄລ​ຍະ​ສັ້ນ ຜົນ​ປະ​ໂຫຍດ​ຂອງ​ພະ​ນັກ​ງານ",
      amount: "",
      remark: "",
    },
    { name: "ໜີ້​ຕ້ອງ​ສົ່ງໝູນ​ວຽນ​ອື່ນໆ", amount: "", remark: "" },
    { name: "ເງິ​ນ​ແຮ-​ໜີ້​ສິນ​ໝູນ​ວຽນ", amount: "", remark: "" },
  ]);

  const [nonRevolvingDebts, setNonRevolvingDebts] = React.useState<
    Array<{
      name: string;
      amount: string;
      remark: string;
    }>
  >([
    {
      name: "ໜີ້​ສິນທາງການ​ເງິນ,ເງິນ​ກູ້​ຢືມໄລ​ຍະ​ຍາວ",
      amount: "",
      remark: "",
    },
    {
      name: "ພັນ​ທະໄລ​ຍະ​ຍາວຜົນ​ປະ​ໂຫຍດ​ຂອງ​ພະ​ນັກ​ງານ",
      amount: "",
      remark: "",
    },
    { name: "ໜີ້​ຕ້ອງ​ສົ່ງບໍ່​ໝູນ​ວຽນ​ອື່ນໆ", amount: "", remark: "" },
    { name: "ເງິນແຮ-ໜີ້ສິນບໍ່ໝູນວຽນ", amount: "", remark: "" },
    { name: "ອາ​ກອນເຍື້ອ​ຊຳ​ລະ", amount: "", remark: "" },
  ]);

  const [equityDebts, setEquityDebts] = React.useState<
    Array<{
      name: string;
      amount: string;
      remark: string;
    }>
  >([
    { name: "ທຶນ​ຈົດ​ທະ​ບຽນ", amount: "", remark: "" },
    { name: "ສ່ວນ​ເ​ພີ່ມ​ມູນ​ຄ່າ​ຮຸ້ນ", amount: "", remark: "" },
    { name: "ຄັງ​ສຳ​ຮອງ", amount: "", remark: "" },
    {
      name: "ຜົນ​ໄດ້​ຮັບ​ຍົກ​ມາ(ກຳ​ໄລຫຼືຂາດ​ທຶນຍົກ​ມາ)",
      amount: "",
      remark: "",
    },
    { name: "ຜົນ​ໄດ້​ຮັບ​ສຸດທິ ໃນປີ", amount: "", remark: "" },
    { name: "ພູດ​ສ່ວນຂອງ​ຜົນ​ປະ​ໂຫຍດ​ສ່ວນ​ນ້ອຍ", amount: "", remark: "" },
    { name: "ພູດ​ສ່ວນຂອງ​ກຸ່ມ", amount: "", remark: "" },
  ]);

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.DEBT_REPORT);

  const mutation = useMutation({
    mutationFn: (data: CreateDebtRequest) => postDebt(data),
    onSuccess: () => {
      toast({
        title: "ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      localStorage.setItem("year", JSON.stringify(year));
      queryClient.invalidateQueries({ queryKey: ["debt", companyId] });
      navigate({ to: "/report/finance/company/finance-debt" });
    },
    onError: (error: Error) => {
      console.error("Error:", error);

      toast({
        title: "ບໍ່ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນບໍ່ສຳເລັດ",
        variant: "destructive",
      });
    },
  });

  const formatNumber = (value: string | number) => {
    if (!value && value !== 0) return "";

    // Convert to string and remove existing formatting
    let stringValue = String(value).replace(/[,\s]/g, "");

    // Handle parentheses
    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    if (isNegative) {
      stringValue = stringValue.slice(1, -1);
    }

    // Split into integer and decimal parts
    const [integerPart, decimalPart] = stringValue.split(".");

    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Combine with decimal part if it exists
    const formattedValue =
      decimalPart !== undefined
        ? `${formattedInteger}.${decimalPart.slice(0, 2)}`
        : formattedInteger;

    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    // Check for empty year
    if (!year || year.toString().trim() === "") {
      setYearError("ກະລຸນາປ້ອນປີ");
      yearInputRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (yearError) return;

    if (!docFile) {
      toast({
        title: "ບັນທຶກລົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
        variant: "destructive",
      });
      return;
    }

    const convertAmount = (amount: string) => {
      if (!amount) return 0;
      const cleanValue = amount.replace(/[,()]/g, "");
      const numValue = Number(cleanValue);
      return amount.includes("(") ? -numValue : numValue;
    };

    const debtDetails = [
      ...equityDebts.map((debt) => ({
        ...debt,
        amount: convertAmount(debt.amount),
        debtType: "EQUITY" as const,
        editRequestStatus: "PENDING",
      })),
      ...revolvingDebts.map((debt) => ({
        ...debt,
        amount: convertAmount(debt.amount),
        debtType: "REVOLVING" as const,
        editRequestStatus: "PENDING",
      })),
      ...nonRevolvingDebts.map((debt) => ({
        ...debt,
        amount: convertAmount(debt.amount),
        debtType: "NON_REVOLVING" as const,
        editRequestStatus: "PENDING",
      })),
    ];

    const data: CreateDebtRequest = {
      companyId,
      yearly: year as number,
      docFile,
      debtDetails,
    };

    mutation.mutate(data);
  };

  // Add handlers for each debt type
  const addDebt = (
    _type: "EQUITY" | "REVOLVING" | "NON_REVOLVING",
    setter: React.Dispatch<
      React.SetStateAction<
        Array<{ name: string; amount: string; remark: string }>
      >
    >
  ) => {
    setter((prev) => [...prev, { name: "", amount: "", remark: "" }]);
  };

  const removeDebt = (
    index: number,
    setter: React.Dispatch<
      React.SetStateAction<
        Array<{ name: string; amount: string; remark: string }>
      >
    >
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // Add sum calculation functions
  const calculateSum = (debts: Array<{ amount: string }>) => {
    return debts.reduce((sum, debt) => {
      if (!debt.amount) return sum;

      const cleanValue = debt.amount.replace(/[,()]/g, "");
      const numValue = Number(cleanValue);

      if (isNaN(numValue)) return sum;

      // If the amount is in parentheses, treat it as negative
      return sum + (debt.amount.includes("(") ? -numValue : numValue);
    }, 0);
  };

  const equitySum = calculateSum(equityDebts);
  const revolvingSum = calculateSum(revolvingDebts);
  const nonRevolvingSum = calculateSum(nonRevolvingDebts);
  const totalSum = equitySum + revolvingSum + nonRevolvingSum;

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: {
                  page: 1,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນໜີ້ສິນ</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Year Input */}
            {/* <div className="flex flex-col gap-2 mb-4" ref={yearInputRef}>
							<Label>ປະຈຳປີ</Label>
							<div className="flex gap-x-2 items-center">
								<Input
									type="number"
									value={year}
									onChange={handleYearChange}
									placeholder="ປ້ອນປີ..."
									className="max-w-[300px]"
									min={1900}
								/>
								{yearError && (
									<span className="text-sm text-red-500">{yearError}</span>
								)}
							</div>
						</div> */}

            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={year ? String(year) : ""}
                onValueChange={(value) => {
                  setYear(Number(value));
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    {
                      length: new Date().getFullYear() - 1974,
                    },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Unit Selector */}

            <Table>
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableHead>ຊື່</TableHead>
                  <TableHead>ມູນຄ່າ</TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>

                  <TableHead className="w-[100px]">ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>I ໜີ້ສິນໝູນວຽນ</TableCell>
                </TableRow>
                {revolvingDebts.map((debt, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={debt.name}
                        onChange={(e) => {
                          const newDebts = [...revolvingDebts];
                          newDebts[index].name = e.target.value;
                          setRevolvingDebts(newDebts);
                        }}
                        readOnly={index < 7}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumber(debt.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newDebts = [...revolvingDebts]; // or nonRevolvingDebts or equityDebts

                          // Allow empty input
                          if (!value) {
                            newDebts[index].amount = "";
                            setRevolvingDebts(newDebts);
                            return;
                          }

                          // Remove existing formatting
                          const rawValue = value.replace(/,/g, "");

                          // Check if the input matches our pattern (including decimals and parentheses)
                          if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
                            newDebts[index].amount = rawValue;
                            setRevolvingDebts(newDebts);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={debt.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) => {
                          const newDebts = [...revolvingDebts];
                          newDebts[index].remark = e.target.value;
                          setRevolvingDebts(newDebts);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {[0, 1, 2, 3, 4, 5, 6].includes(index) ? null : (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeDebt(index, setRevolvingDebts)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant={"ghost"}
                  className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
                  size="icon"
                  onClick={() => addDebt("REVOLVING", setRevolvingDebts)}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ໜີ້ສິນໝູນວຽນ I</TableCell>
                  <TableCell className="text-right">
                    {revolvingSum ? revolvingSum.toLocaleString() : ""}
                  </TableCell>
                  <TableCell colSpan={2}> </TableCell>
                </TableRow>
                {/* Non-Revolving Debts */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>II ໜີ້ສິນບໍ່ໝູນວຽນ</TableCell>
                </TableRow>
                {nonRevolvingDebts.map((debt, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={debt.name}
                        onChange={(e) => {
                          const newDebts = [...nonRevolvingDebts];
                          newDebts[index].name = e.target.value;
                          setNonRevolvingDebts(newDebts);
                        }}
                        readOnly={index < 5}
                      />
                    </TableCell>
                    <TableCell>
                      {/* <Input
												type="text"
												value={formatNumber(debt.amount)}
												onChange={(e) => {
													const value = e.target.value.replace(/,/g, "");
													const numValue = value === "" ? 0 : Number(value);
													if (!isNaN(numValue)) {
														const newDebts = [...nonRevolvingDebts];
														newDebts[index].amount = numValue;
														setNonRevolvingDebts(newDebts);
													}
												}}
												className="text-right"
											/> */}
                      <Input
                        type="text"
                        value={formatNumber(debt.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newDebts = [...nonRevolvingDebts]; // or nonRevolvingDebts or equityDebts

                          // Allow empty input
                          if (!value) {
                            newDebts[index].amount = "";
                            setNonRevolvingDebts(newDebts);
                            return;
                          }

                          // Remove existing formatting
                          const rawValue = value.replace(/,/g, "");

                          // Check if the input matches our pattern (including decimals and parentheses)
                          if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
                            newDebts[index].amount = rawValue;
                            setNonRevolvingDebts(newDebts);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={debt.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) => {
                          const newDebts = [...nonRevolvingDebts];
                          newDebts[index].remark = e.target.value;
                          setNonRevolvingDebts(newDebts);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {[0, 1, 2, 3, 4].includes(index) ? null : (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() =>
                            removeDebt(index, setNonRevolvingDebts)
                          }
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant={"ghost"}
                  className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
                  size="icon"
                  onClick={() => addDebt("NON_REVOLVING", setNonRevolvingDebts)}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ໜີ້ສິນບໍ່ໝູນວຽນ II</TableCell>
                  <TableCell className="text-right">
                    {nonRevolvingSum ? nonRevolvingSum.toLocaleString() : ""}
                  </TableCell>
                  <TableCell colSpan={2}> </TableCell>
                </TableRow>

                {/* Equity Debts */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>III ທຶນຕົນເອງ</TableCell>
                </TableRow>
                {equityDebts.map((debt, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={debt.name}
                        onChange={(e) => {
                          const newDebts = [...equityDebts];
                          newDebts[index].name = e.target.value;
                          setEquityDebts(newDebts);
                        }}
                        readOnly={index < 7}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumber(debt.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newDebts = [...equityDebts]; // or nonRevolvingDebts or equityDebts

                          // Allow empty input
                          if (!value) {
                            newDebts[index].amount = "";
                            setEquityDebts(newDebts);
                            return;
                          }

                          // Remove existing formatting
                          const rawValue = value.replace(/,/g, "");

                          // Check if the input matches our pattern (including decimals and parentheses)
                          if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
                            newDebts[index].amount = rawValue;
                            setEquityDebts(newDebts);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={debt.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) => {
                          const newDebts = [...equityDebts];
                          newDebts[index].remark = e.target.value;
                          setEquityDebts(newDebts);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {[0, 1, 2, 3, 4, 5, 6].includes(index) ? null : (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeDebt(index, setEquityDebts)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant={"ghost"}
                  className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
                  size="icon"
                  onClick={() => addDebt("EQUITY", setEquityDebts)}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ລວມທຶນຕົນເອງ (III)</TableCell>
                  <TableCell className="text-right">
                    {equitySum ? equitySum.toLocaleString() : ""}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
                {/* Revolving Debts */}

                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ລວມຍອດໜີ້ສິນທັງໝົດ ( I + II + III )</TableCell>
                  <TableCell className="text-right">
                    {totalSum ? totalSum.toLocaleString() : ""}
                  </TableCell>
                  <TableCell colSpan={2}> </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* File Input */}
            <div className="flex gap-x-1 items-center mt-4">
              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-x-2 justify-end mt-4">
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/report/finance",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  })
                }
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" className="flex gap-x-2 items-center">
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
